import type { FC } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { EditSolidIcon } from '@dhl-ui/icons'
import { Box, Body2, Card, Grid, H6, IconButton } from '@dhl-ui/components'
import { links } from '~/AppRoutes'
import type { ServicepointVendor } from '~/modules/servicepointvendors/model'

export interface Props {
  servicepoint: ServicepointVendor
}

type Params = {
  id: string
}
export const ServicepointVendorDetails: FC<Props> = ({ servicepoint }) => {
  const navigate = useNavigate()
  const { id: userId } = useParams<Params>()
  const { t } = useTranslation()

  return (
    <Card>
      <Card.Header
        title={t('admin.servicePointVendorsPage.servicePointVendorDetails.profile')}
        endNode={
          userId ? (
            <IconButton
              aria-label="edit servicepoint"
              onClick={() => navigate(links.editServicePointVendorLink(userId))}
            >
              <EditSolidIcon data-testid="edit-servicepoints" />
            </IconButton>
          ) : undefined
        }
      />
      <Card.Content>
        <H6>{t('admin.servicePointVendorsPage.servicePointVendorDetails.details')}</H6>
        <Grid container direction="row" data-testid="servicepointDetails">
          <Grid size={{ xs: 12, sm: 6 }}>
            <Box pt={2}>
              <Body2 variant="tertiary">
                {t('admin.servicePointVendorsPage.servicePointVendorDetails.vendorName')}
              </Body2>
              <Body2>{servicepoint?.name}</Body2>
            </Box>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Box pt={2}>
              <Body2 variant="tertiary">{t('admin.servicePointVendorsPage.servicePointVendorDetails.country')}</Body2>
              <Body2>{servicepoint?.address.countryCode}</Body2>
            </Box>
          </Grid>
        </Grid>
        <Grid container direction="row">
          <Grid size={{ xs: 12, sm: 6 }}>
            <Box pt={2}>
              <Body2 variant="tertiary">
                {t('admin.servicePointVendorsPage.servicePointVendorDetails.externalId')}
              </Body2>
              <Body2>{servicepoint?.externalId}</Body2>
            </Box>
          </Grid>
        </Grid>
        <Box pt={4}>
          <Box>
            <H6>{t('admin.servicePointVendorsPage.servicePointVendorDetails.addressInfo')}</H6>
            <Grid container direction="row">
              <Grid size={{ xs: 12, sm: 4 }}>
                <Box pt={2}>
                  <Body2 variant="tertiary">
                    {t('admin.servicePointVendorsPage.servicePointVendorDetails.postalCode')}
                  </Body2>
                  <Body2>{servicepoint?.address?.postalCode}</Body2>
                </Box>
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <Box pt={2}>
                  <Body2 variant="tertiary">
                    {t('admin.servicePointVendorsPage.servicePointVendorDetails.houseNumber')}
                  </Body2>
                  <Body2>{servicepoint?.address?.houseNumber}</Body2>
                </Box>
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <Box pt={2}>
                  <Body2 variant="tertiary">
                    {t('admin.servicePointVendorsPage.servicePointVendorDetails.addition')}
                  </Body2>
                  <Body2>
                    {servicepoint?.address?.houseNumberSuffix ? servicepoint?.address?.houseNumberSuffix : '-'}
                  </Body2>
                </Box>
              </Grid>
            </Grid>
            <Grid container direction="row">
              <Grid size={{ xs: 12, sm: 4 }}>
                <Box pt={2}>
                  <Body2 variant="tertiary">
                    {t('admin.servicePointVendorsPage.servicePointVendorDetails.street')}
                  </Body2>
                  <Body2>{servicepoint?.address?.street}</Body2>
                </Box>
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <Box pt={2}>
                  <Body2 variant="tertiary">{t('admin.servicePointVendorsPage.servicePointVendorDetails.city')}</Body2>
                  <Body2>{servicepoint?.address?.city}</Body2>
                </Box>
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <Box pt={2}>
                  <Body2 variant="tertiary">
                    {t('admin.servicePointVendorsPage.servicePointVendorDetails.country')}
                  </Body2>
                  <Body2>{servicepoint?.address?.countryCode}</Body2>
                </Box>
              </Grid>
            </Grid>
            <Grid container direction="row">
              <Grid size={{ xs: 12, sm: 4 }}>
                <Box pt={2}>
                  <Body2 variant="tertiary">
                    {t('admin.servicePointVendorsPage.servicePointVendorDetails.popular')}
                  </Body2>
                  <Body2>{servicepoint?.isPopular ? 'YES' : 'NO'}</Body2>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Box>
      </Card.Content>
    </Card>
  )
}
